import { useEffect, useState, useCallback, useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  Eye,
  MessageSquare,
  GraduationCap,
  BadgePoundSterling,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ShieldCheck,
  UserPlus,
  Radar,
  Bell,
  CreditCard,
  Globe,
  Sparkles,
  AlertTriangle,
  LogIn,
  Activity,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const ICONS = {
  shield_check:    ShieldCheck,
  user_plus:       UserPlus,
  radar:           Radar,
  bell:            Bell,
  credit_card:     CreditCard,
  globe:           Globe,
  sparkles:        Sparkles,
  alert_triangle:  AlertTriangle,
  log_in:          LogIn,
  eye:             Eye,
};

const STAT_ICON = {
  page_views_today:  Eye,
  new_enquiries_7d:  MessageSquare,
  enrolments_30d:    GraduationCap,
  revenue_30d:       BadgePoundSterling,
};

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

function formatValue(stat) {
  if (stat.format === 'currency') {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: stat.currency || 'GBP',
      maximumFractionDigits: 0,
    }).format(stat.value);
  }
  return new Intl.NumberFormat('en-GB').format(stat.value);
}

function StatCard({ statKey, stat }) {
  const Icon = STAT_ICON[statKey] || Activity;
  const isUp = stat.delta_direction === 'up';
  const Trend = isUp ? TrendingUp : TrendingDown;
  const trendColor = isUp ? 'var(--ok)' : 'var(--danger)';
  const trendBg = isUp ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)';

  return (
    <div
      data-testid={`admin-stat-${statKey}`}
      className="p-5 rounded-lg relative overflow-hidden"
      style={CARD_STYLE}
    >
      <div className="flex items-start justify-between mb-4">
        <p
          className="font-mono text-[10px] uppercase tracking-[0.2em]"
          style={{ color: 'var(--ts)' }}
        >
          {stat.label}
          {stat.period && <span style={{ color: 'var(--te)' }}> · {stat.period}</span>}
        </p>
        <div
          className="flex items-center justify-center rounded"
          style={{
            width: 28,
            height: 28,
            background: 'var(--a-glow)',
            border: '1px solid var(--a-border)',
          }}
        >
          <Icon size={14} style={{ color: 'var(--a)' }} />
        </div>
      </div>

      <p className="font-mono text-3xl font-semibold mb-3" style={{ color: 'var(--white)' }}>
        {formatValue(stat)}
      </p>

      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
          style={{ background: trendBg, color: trendColor }}
        >
          <Trend size={11} />
          <span className="font-mono text-[11px]">
            {stat.delta_pct > 0 ? '+' : ''}{stat.delta_pct.toFixed(1)}%
          </span>
        </span>
        <span className="font-body text-[11px]" style={{ color: 'var(--ts)' }}>
          {stat.comparison}
        </span>
      </div>
    </div>
  );
}

function ActivityItem({ item }) {
  const Icon = ICONS[item.icon] || Activity;
  return (
    <li className="flex items-start gap-3 py-3" style={{ borderBottom: '1px solid var(--b1)' }}>
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-full mt-0.5"
        style={{
          width: 28,
          height: 28,
          background: 'var(--a-glow)',
          border: '1px solid var(--a-border)',
        }}
      >
        <Icon size={12} style={{ color: 'var(--a)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em]" style={{ color: 'var(--a)' }}>
          {item.action.replaceAll('_', ' · ')}
        </p>
        <p className="font-body text-sm mt-0.5 truncate" style={{ color: 'var(--white)' }}>
          {item.description}
        </p>
        <p className="font-mono text-[10px] mt-1" style={{ color: 'var(--te)' }}>
          {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
        </p>
      </div>
    </li>
  );
}

function CourseBar({ course, maxViews }) {
  const pct = Math.round((course.views / maxViews) * 100);
  return (
    <div
      data-testid={`admin-course-${course.id}`}
      className="py-3"
      style={{ borderBottom: '1px solid var(--b1)' }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="font-mono text-[10px] w-5 text-center"
            style={{ color: 'var(--ts)' }}
          >
            {String(course.rank).padStart(2, '0')}
          </span>
          <span className="font-body text-sm truncate" style={{ color: 'var(--white)' }}>
            {course.title}
          </span>
        </div>
        <span className="font-mono text-xs" style={{ color: 'var(--tm)' }}>
          {new Intl.NumberFormat('en-GB').format(course.views)}
        </span>
      </div>
      <div
        className="h-1.5 rounded-full overflow-hidden"
        style={{ background: 'var(--b1)' }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: 'linear-gradient(90deg, var(--a), var(--violet))',
          }}
        />
      </div>
    </div>
  );
}

function Sparkline({ data }) {
  const maxV = useMemo(() => Math.max(...data.map((d) => d.visits)) || 1, [data]);
  // Layout
  const barWidth = 40;
  const gap = 18;
  const svgW = data.length * barWidth + (data.length - 1) * gap;
  const svgH = 140;
  const topPad = 12;
  const labelPad = 24;
  const chartH = svgH - topPad - labelPad;

  return (
    <svg
      viewBox={`0 0 ${svgW} ${svgH}`}
      width="100%"
      height={svgH}
      role="img"
      aria-label="Site traffic, last 7 days"
      data-testid="admin-sparkline"
    >
      {data.map((d, i) => {
        const h = Math.max(4, (d.visits / maxV) * chartH);
        const x = i * (barWidth + gap);
        const y = topPad + (chartH - h);
        return (
          <g key={d.date}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={h}
              rx={3}
              fill={d.is_today ? 'var(--a)' : 'var(--a-dim)'}
              opacity={d.is_today ? 1 : 0.55}
            >
              <title>{`${d.day} · ${d.date} · ${d.visits} visits`}</title>
            </rect>
            <text
              x={x + barWidth / 2}
              y={y - 4}
              textAnchor="middle"
              className="font-mono"
              fontSize="10"
              fill={d.is_today ? 'var(--white)' : 'var(--ts)'}
            >
              {d.visits}
            </text>
            <text
              x={x + barWidth / 2}
              y={svgH - 6}
              textAnchor="middle"
              className="font-mono"
              fontSize="10"
              fill={d.is_today ? 'var(--a)' : 'var(--te)'}
              style={{ letterSpacing: '0.1em' }}
            >
              {d.day.toUpperCase()}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function DashboardSection() {
  const { adminFetch } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const url = `${BACKEND_URL}/api/admin/dashboard${refresh ? '?refresh=true' : ''}`;
      const res = await adminFetch(url);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(false); }, [load]);

  if (loading && !data) {
    return (
      <div className="p-8 rounded-lg" style={CARD_STYLE}>
        <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>Loading dashboard…</p>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-8 rounded-lg" style={CARD_STYLE}>
        <p className="font-mono text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
      </div>
    );
  }
  if (!data) return null;

  const { stats, activity, courses, traffic_7d, generated_at } = data;
  const maxCourseViews = Math.max(...courses.map((c) => c.views));

  return (
    <div className="space-y-5" data-testid="admin-dashboard-section">
      {/* Header strip */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            section · dashboard
          </p>
          <h2 className="font-display text-2xl font-bold mt-1" style={{ color: 'var(--white)' }}>
            Platform Overview
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px]" style={{ color: 'var(--te)' }}>
            generated {formatDistanceToNow(new Date(generated_at), { addSuffix: true })}
          </span>
          <button
            data-testid="admin-dashboard-refresh"
            onClick={() => load(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 font-body text-xs px-3 py-1.5 rounded-[3px]"
            style={{
              color: 'var(--tm)',
              border: '1px solid var(--b1)',
              background: 'transparent',
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* 4 stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stat-grid">
        {Object.entries(stats).map(([k, s]) => (
          <StatCard key={k} statKey={k} stat={s} />
        ))}
      </div>

      {/* Activity + Courses */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div
          className="lg:col-span-3 p-6 rounded-lg"
          style={CARD_STYLE}
          data-testid="admin-activity-feed"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
                feed
              </p>
              <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
                Recent Activity
              </h3>
            </div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--te)' }}>
              last {activity.length}
            </span>
          </div>
          <ul>{activity.map((a) => <ActivityItem key={a.id} item={a} />)}</ul>
        </div>

        <div
          className="lg:col-span-2 p-6 rounded-lg"
          style={CARD_STYLE}
          data-testid="admin-course-list"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
                ranked
              </p>
              <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
                Most Viewed Courses
              </h3>
            </div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--te)' }}>
              30d
            </span>
          </div>
          <div>
            {courses.map((c) => (
              <CourseBar key={c.id} course={c} maxViews={maxCourseViews} />
            ))}
          </div>
        </div>
      </div>

      {/* Sparkline */}
      <div className="p-6 rounded-lg" style={CARD_STYLE} data-testid="admin-traffic-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
              trend
            </p>
            <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
              Site Traffic · Last 7 Days
            </h3>
          </div>
          <div className="flex items-center gap-3 font-mono text-[10px]" style={{ color: 'var(--ts)' }}>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: 'var(--a)' }} />
              today
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block w-2 h-2 rounded-sm"
                style={{ background: 'var(--a-dim)', opacity: 0.55 }}
              />
              prior
            </span>
          </div>
        </div>
        <Sparkline data={traffic_7d} />
      </div>
    </div>
  );
}
